import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

export const getRowId = <T,>(original: T, index: number) => {
	const org = original as Record<string, unknown>;
	if (org && org.id) {
		return `${org.id}`;
	}
	return `${index}`;
};

type ColumnDef<T> = {
	accessorKey: string;
	header: string;
	size?: number;
	minSize?: number;
	enableResizing?: boolean;
	cell?: (context: { getValue: () => unknown; row: { original: T } }) => React.ReactNode;
};

type RowSelectionState = Record<string, boolean>;

type TableProps<T> = {
	data: T[];
	columns: ColumnDef<T>[];
	filter?: string;
	onRowClick?: (row: T) => void;
	enableRowSelection?: boolean | ((row: T, index: number) => boolean);
	setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
	rowSelection?: RowSelectionState;
};

// Memoize TableRow component to prevent unnecessary re-renders
const TableRow = memo(
	<T,>({
		row,
		rowIndex,
		rowId,
		columns,
		isSelected,
		canSelect,
		enableRowSelection,
		onClickTableRow,
		handleRowSelection,
	}: {
		row: T;
		rowIndex: number;
		rowId: string;
		columns: ColumnDef<T>[];
		isSelected: boolean;
		canSelect: boolean;
		enableRowSelection: boolean | ((row: T, index: number) => boolean);
		onClickTableRow: (row: T) => void;
		handleRowSelection: (rowId: string, checked: boolean) => void;
	}) => {
		return (
			<tr
				key={rowId}
				onClick={() => onClickTableRow(row)}
				className={`min-h-[28px] break-words border-b border-common-light text-[13px] font-light text-common-light ${
					isSelected ? 'bg-primary-light' : ''
				}`}
			>
				{enableRowSelection && (
					<th className="items-center text-sm text-common-light">
						{canSelect && (
							<input
								type="checkbox"
								checked={isSelected}
								onChange={(e) => handleRowSelection(rowId, e.target.checked)}
								onClick={(e) => e.stopPropagation()}
							/>
						)}
					</th>
				)}
				{columns.map((column) => {
					const value = row ? (row as Record<string, unknown>)[column.accessorKey] : undefined;
					const cellContext = {
						getValue: () => value,
						row: { original: row },
						rowIndex,
					};

					return (
						<td key={column.accessorKey} className="p-2 align-top">
							<div className="break-words whitespace-normal leading-tight">
								{column.cell ? column.cell(cellContext) : String(value ?? '')}
							</div>
						</td>
					);
				})}
			</tr>
		);
	},
) as <T>(props: {
	row: T;
	rowIndex: number;
	rowId: string;
	columns: ColumnDef<T>[];
	isSelected: boolean;
	canSelect: boolean;
	enableRowSelection: boolean | ((row: T, index: number) => boolean);
	onClickTableRow: (row: T) => void;
	handleRowSelection: (rowId: string, checked: boolean) => void;
}) => React.ReactElement;

const BasicTable = <T,>(props: TableProps<T>) => {
	const { data, columns, filter = '', onRowClick, enableRowSelection = false, rowSelection = {}, setRowSelection } = props;

	const [sortConfig, setSortConfig] = useState<{
		key: string | null;
		direction: 'asc' | 'desc';
	}>({ key: null, direction: 'asc' });

	// Optimize filter with early exit and cached lowercase filter
	const filteredData = useMemo(() => {
		if (!filter || filter.trim() === '') {
			return data;
		}

		const lowerFilter = filter.toLowerCase().trim();

		// Pre-compute which columns to search
		const searchableColumns = columns.map((col) => col.accessorKey);

		return data.filter((row) => {
			const rowData = row as Record<string, unknown>;

			// Early exit if any column matches
			for (const key of searchableColumns) {
				const value = rowData[key];
				if (value !== null && value !== undefined) {
					const strValue = String(value);
					// Use indexOf for better performance than includes
					if (strValue.toLowerCase().indexOf(lowerFilter) !== -1) {
						return true;
					}
				}
			}
			return false;
		});
	}, [data, filter, columns]);

	// Sort data
	const sortedData = useMemo(() => {
		if (!sortConfig.key) {
			return filteredData;
		}

		const sorted = [...filteredData].sort((a, b) => {
			const key = sortConfig.key;
			if (!key) {
				return 0;
			}

			const aValue = (a as Record<string, unknown>)[key];
			const bValue = (b as Record<string, unknown>)[key];

			if (aValue === null || aValue === undefined) {
				return 1;
			}
			if (bValue === null || bValue === undefined) {
				return -1;
			}

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
			}

			if (aValue < bValue) {
				return sortConfig.direction === 'asc' ? -1 : 1;
			}
			if (aValue > bValue) {
				return sortConfig.direction === 'asc' ? 1 : -1;
			}
			return 0;
		});

		return sorted;
	}, [filteredData, sortConfig]);

	const handleSort = (columnKey: string) => {
		setSortConfig((current) => {
			if (current.key === columnKey) {
				return {
					key: columnKey,
					direction: current.direction === 'asc' ? 'desc' : 'asc',
				};
			}
			return { key: columnKey, direction: 'asc' };
		});
	};

	const handleRowSelection = useCallback(
		(rowId: string, checked: boolean) => {
			if (setRowSelection) {
				setRowSelection((prev) => ({
					...prev,
					[rowId]: checked,
				}));
			}
		},
		[setRowSelection],
	);

	const handleSelectAll = useCallback(
		(checked: boolean) => {
			if (setRowSelection) {
				const newSelection: RowSelectionState = {};
				if (checked) {
					sortedData.forEach((row, index) => {
						const rowId = getRowId(row, index);
						newSelection[rowId] = true;
					});
				}
				setRowSelection(newSelection);
			}
		},
		[setRowSelection, sortedData],
	);

	const isAllSelected = useMemo(() => {
		if (sortedData.length === 0) {
			return false;
		}
		return sortedData.every((row, index) => {
			const rowId = getRowId(row, index);
			return rowSelection[rowId];
		});
	}, [sortedData, rowSelection]);

	const onClickTableRow = useCallback(
		(row: T) => {
			if (onRowClick) {
				onRowClick(row);
			}
		},
		[onRowClick],
	);

	const canSelectRow = useCallback(
		(row: T, index: number): boolean => {
			if (typeof enableRowSelection === 'function') {
				return enableRowSelection(row, index);
			}
			return enableRowSelection === true;
		},
		[enableRowSelection],
	);

	// Virtual scrolling implementation
	const containerRef = useRef<HTMLDivElement>(null);
	const [scrollTop, setScrollTop] = useState(0);

	const ROW_HEIGHT = 28; // Height of each row in pixels
	const BUFFER_SIZE = 5; // Number of extra rows to render above and below viewport

	// Calculate visible range
	const visibleRange = useMemo(() => {
		const containerHeight = containerRef.current?.clientHeight || 500;
		const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_SIZE);
		const endIndex = Math.min(sortedData.length, Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE);

		return { startIndex, endIndex };
	}, [scrollTop, sortedData.length]);

	const visibleData = useMemo(() => {
		return sortedData.slice(visibleRange.startIndex, visibleRange.endIndex);
	}, [sortedData, visibleRange]);

	const offsetY = visibleRange.startIndex * ROW_HEIGHT;

	const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
		setScrollTop(e.currentTarget.scrollTop);
	}, []);

	return (
		<div
			ref={containerRef}
			className="table-container invisible-scrollbar h-full w-full overflow-auto border border-common-light"
			onScroll={handleScroll}
		>
			<table
				className="relative w-full"
				style={{
					tableLayout: 'fixed',
					borderCollapse: 'collapse',
				}}
			>
				<thead className="sticky top-0 z-10 h-[28px] border-b border-common-light bg-primary-light">
					<tr>
						{/* Checkbox để chọn tất cả các hàng */}
						{enableRowSelection && (
							<th className="items-center text-sm text-common-light">
								<div>
									<input type="checkbox" checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
								</div>
							</th>
						)}
						{columns.map((column) => (
							<th
								className="items-left cursor-pointer px-2 text-sm text-common-light"
								key={column.accessorKey}
								style={{
									width: column.size ? `${column.size}px` : 'auto',
								}}
								onClick={() => handleSort(column.accessorKey)}
							>
								<div className="flex w-full justify-left">
									{column.header}
									{sortConfig.key === column.accessorKey && (
										<span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
									)}
								</div>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{/* Spacer for virtual scrolling - only if needed */}
					{offsetY > 0 && (
						<tr style={{ height: `${offsetY}px` }}>
							<td colSpan={columns.length + (enableRowSelection ? 1 : 0)} style={{ padding: 0, border: 0 }} />
						</tr>
					)}

					{visibleData.map((row, idx) => {
						const rowIndex = visibleRange.startIndex + idx;
						const rowId = getRowId(row, rowIndex);
						const isSelected = rowSelection[rowId] || false;
						const canSelect = canSelectRow(row, rowIndex);

						return (
							<TableRow
								key={rowId}
								row={row}
								rowIndex={rowIndex}
								rowId={rowId}
								columns={columns}
								isSelected={isSelected}
								canSelect={canSelect}
								enableRowSelection={enableRowSelection}
								onClickTableRow={onClickTableRow}
								handleRowSelection={handleRowSelection}
							/>
						);
					})}

					{/* Bottom spacer for remaining rows */}
					{visibleRange.endIndex < sortedData.length && (
						<tr style={{ height: `${(sortedData.length - visibleRange.endIndex) * ROW_HEIGHT}px` }}>
							<td colSpan={columns.length + (enableRowSelection ? 1 : 0)} style={{ padding: 0, border: 0 }} />
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
};

export default BasicTable;
